import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { IconButton } from "../../components/IconButton/IconButton";
import { errorLog } from "../../utils/logger";

export function EnterPinForm() {
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const [search, setSearch] = useAtom(selectedSearchAtom);
  const [showErrorModal, setShowErrorModal] = useState<boolean | string>(false);
  const [pin, setPin] = useState("");

  const navigate = useNavigate();
  const { t } = useTranslation();

  const mobilePinInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mobilePinInputRef.current) {
      mobilePinInputRef.current.focus();
    }
  }, []);

  const getInfoText = () => {
    return t("immunizations.pin_entry_header2_alt").split("|");
  };

  const continueFromAlert = async () => {
    if (pin.length < 8 || search === null) {
      return;
    }

    try {
      await getAPIClient().verifyImmunizationSearchPIN(
        search.uid,
        { pin: pin },
        search.izProviderKey
      );
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to navigate immediately.
       */
      fetchRecordsUntilUpdated();
      navigate("../results", { state: { isScenarioB: false } });
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const formatPin = (value: string) => {
    return value.replace(/^(\d{0,4})(\d{0,4})$/, (_, g1, g2) => (g2 ? `${g1}-${g2}` : g1));
  };

  const onPinChange = (value: string) => {
    let cleaned = value.replace(/\D/g, "");
    if (cleaned.length > 8) cleaned = cleaned.slice(0, 8);
    setPin(cleaned);
  };

  const deleteSearch = async () => {
    if (search === null) {
      // In the event that the user somehow navigates to this page without a search,
      // lets just go through the escape hatch to avoid unintentional behavior.
      navigate("/home/search");
      return;
    }

    try {
      await getAPIClient().deleteIzSearch(search.uid, search.izProviderKey);
      const updatedSearches = await getAPIClient().getIzSearches(false);
      await setIzSearchAPIResult(updatedSearches.data);
      await setSearch(null);
      navigate("/home/search");
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  return (
    <div className="flex flex-col docket-gap-1 max-md:px-6 max-md:mt-2">
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      <div className="relative flex items-center justify-center w-full max-md:hidden">
        <p className="text-dark dark:text-white max-md:hidden text-center">
          {t("immunizations.pin_entry_header")}
        </p>
        <IconButton
          dataTestId="deleteBtn-desktop"
          icon="trash"
          onClick={() => deleteSearch()}
          alt="delete"
          className="absolute right-0"
        />
      </div>
      <div className="md:hidden top-[20px] right-[20px] fixed z-50">
        <IconButton
          dataTestId="deleteBtn-mobile"
          icon="trash"
          onClick={() => deleteSearch()}
          alt="delete"
        />
      </div>
      <input
        className="input border border-black mt-10 text-black w-full md:hidden max-md:!placeholder-dark-blue max-md:dark:!placeholder-white max-md:text-lg max-md:border-border-gray max-md:py-8 max-md:text-white"
        type="tel"
        inputMode="tel"
        pattern="[0-9]*"
        placeholder="Enter Code"
        data-testid="pin-mobile"
        maxLength={9}
        value={formatPin(pin)}
        onChange={(event) => onPinChange(event.target.value)}
        ref={mobilePinInputRef}
      />
      <input
        className="input input-primary input-responsive text-black w-full max-md:hidden"
        type="numeric"
        placeholder="Enter Code"
        data-testid="pin-desktop"
        pattern="[0-9]*"
        maxLength={9}
        value={formatPin(pin)}
        onChange={(event) => onPinChange(event.target.value)}
      />
      <button
        type="submit"
        className="btn docket-button-responsive ml-auto w-fit md:hidden"
        onClick={() => continueFromAlert()}
        {...(pin.length < 8 && { disabled: true })}
      >
        {t("generic.verify")}
      </button>
      <p className="text-white mx-auto md:hidden text-xl font-black">
        {t("immunizations.pin_entry_header")}
      </p>
      <p className="text-dark dark:text-white max-md:text-white max-md:text-sm">
        {getInfoText().join(" ")}
      </p>
      <button
        type="submit"
        data-testid="verify-desktop"
        className="btn docket-button-responsive w-fit max-md:hidden"
        onClick={() => continueFromAlert()}
        {...(pin.length < 8 && { disabled: true })}
      >
        {t("generic.verify")}
      </button>
    </div>
  );
}

export default EnterPinForm;
